import { Directive, ElementRef, Input, OnInit } from '@angular/core';
import { PermissionService } from '../services/permission.service';

/**
 * Stage 4 PR 10 FE — Phase 3C permission directive port, 2026-05-14.
 *
 * Hides the host element when the current user satisfies NONE of the
 * listed permission requirements.
 *
 * Usage:
 *   <button appPermission [permissionArray]="[['claims', 3]]">
 *     Adjudicate Claim
 *   </button>
 *
 * Semantics — OR across the array. The element stays visible if the
 * user satisfies AT LEAST ONE listed permission. If all of them
 * fail, the element is hidden (display: none).
 *
 * Restores Atenza's verbose-switch shape per the PR 10 rescope
 * decision. PR 4.1's "SUPER_ADMIN bypass + generic dictionary
 * fallback" was the temporary hotfix that kept the sidebar usable
 * while BE permission columns were missing — PR 10 added real
 * columns for every key and PR 10 FE restores the explicit
 * switch in PermissionService.
 *
 * Added: empty-array guard so an element marked `[appPermission]`
 * with no requirements declared stays visible by default (avoids
 * the `every() over [] returns true` quirk that hides nothing
 * when you actually want a no-op).
 */
@Directive({
  selector: '[appPermission]'
})
export class PermissionDirective implements OnInit {

  @Input() permissionArray!: Array<Array<any>>;

  constructor(private elementRef: ElementRef, private permissionService: PermissionService) {
  }

  ngOnInit() {
    // No requirements declared — show by default rather than hiding.
    if (!this.permissionArray || this.permissionArray.length === 0) {
      return;
    }

    const hasAnyPermission = this.permissionArray.some((perm) =>
      this.permissionService.checkPermissionsForSection(perm[0], perm[1])
    );

    if (!hasAnyPermission) {
      this.elementRef.nativeElement.style.display = 'none';
    }
  }
}
